import React, { useEffect } from "react";

const Section = ({ id, label, value, onToggle, onText, icon, isEditing=true }) => (
  <div className="exam-section">
    <label className="exam-header" style={{ display: "flex", alignItems: "center", width: "100%", gap: "8px" }}>
      <span style={{ marginRight: 8 }}>{icon}</span>
      <span style={{ flex: 1 }}>{label}</span> 
      <input 
        type="checkbox" 
        checked={value.on === 1} 
        onChange={(e) => onToggle(id, e.target.checked ? 1 : 0)} 
        style={{ alignSelf: "center" }} // Centra el checkbox verticalmente
        disabled={!isEditing}
      />
    </label>
    {value.on === 1 && (
      <textarea 
        rows={2} 
        value={value.text || ""} 
        onChange={(e) => onText(id, e.target.value)} 
        placeholder="Finding..." 
        disabled={!isEditing}
      />
    )}
  </div>
);

export default function ExamFormTemplates({ exam, onChange, isEditing }) {
  const toggle = (k, v) => onChange({ ...exam, [k]: { ...exam[k], on: v } });
  const setTxt = (k, v) => onChange({ ...exam, [k]: { ...exam[k], text: v, on: v.trim() ? 1 : exam[k].on } }); // Activa check si hay texto

  // Sincronizar 'on' si hay texto al cargar
  useEffect(() => {
    Object.keys(exam).forEach((key) => {
      if (exam[key].text && exam[key].text.trim() && exam[key].on !== 1) {
        toggle(key, 1);
      }
    });
  }, [exam]); // Ejecutar al cargar exam

  // Mapeo de iconos para cada sección del examen físico
  const sectionIcons = {
    GeneralAppearance: "🙂",
    EyesBL: "👀",
    Ears: "👂",
    Nose: "👃",
    OralCavity: "👄",
    Throat: "🗣️",
    NeckThyroid: "🧈",
    Heart: "❤️",
    Lungs: "🫁",
    Breast: "👙",
    Abdomen: "🫀",
    Pelvic: "🩲",
    back: "🧍",
    Musculoskeletal: "🦴",
    Skin: "🧴",
    Neurologic: "🧠",
    Extremities: "🦵",
    Psychiatric: "🧠"
  };

  // Etiquetas más legibles para las secciones
  const sectionLabels = {
    GeneralAppearance: "General Appearance",
    EyesBL: "Eyes",
    Ears: "Ears",
    Nose: "Nose",
    OralCavity: "Oral Cavity",
    Throat: "Throat",
    NeckThyroid: "Neck/Thyroid",
    Heart: "Heart",
    Lungs: "Lungs",
    Breast: "Breast",
    Abdomen: "Abdomen",
    Pelvic: "Pelvic",
    back: "Back",
    Musculoskeletal: "Musculoskeletal",
    Skin: "Skin",
    Neurologic: "Neurologic",
    Extremities: "Extremities",
    Psychiatric: "Psychiatric"
  };

  return (
    <div className="cn-card">
      <h3>🩺 Physical Examination</h3>
      <h4>{sectionIcons.GeneralAppearance} General Appearance</h4>
      <Section
        id="GeneralAppearance"
        label={sectionLabels.GeneralAppearance}
        value={exam.GeneralAppearance || { on: 0, text: "" }}
        onToggle={toggle}
        onText={setTxt}
        icon={sectionIcons.GeneralAppearance}
        isEditing={isEditing}
      />

      <h4>{sectionIcons.EyesBL} Eyes</h4>
      <Section
        id="EyesBL"
        label={sectionLabels.EyesBL}
        value={exam.EyesBL || { on: 0, text: "" }}
        onToggle={toggle}
        onText={setTxt}
        icon={sectionIcons.EyesBL}
        isEditing={isEditing}
      />

      <h4>{sectionIcons.Ears} Ears</h4>
      <Section
        id="Ears"
        label={sectionLabels.Ears}
        value={exam.Ears || { on: 0, text: "" }}
        onToggle={toggle}
        onText={setTxt}
        icon={sectionIcons.Ears}
        isEditing={isEditing}
      />

      <h4>{sectionIcons.Nose} Nose</h4>
      <Section
        id="Nose"
        label={sectionLabels.Nose}
        value={exam.Nose || { on: 0, text: "" }}
        onToggle={toggle}
        onText={setTxt}
        icon={sectionIcons.Nose}
        isEditing={isEditing}
      />

      <h4>{sectionIcons.OralCavity} Oral Cavity</h4>
      <Section
        id="OralCavity"
        label={sectionLabels.OralCavity}
        value={exam.OralCavity || { on: 0, text: "" }}
        onToggle={toggle}
        onText={setTxt}
        icon={sectionIcons.OralCavity}
        isEditing={isEditing}
      />

      <h4>{sectionIcons.Throat} Throat</h4>
      <Section
        id="Throat"
        label={sectionLabels.Throat}
        value={exam.Throat || { on: 0, text: "" }}
        onToggle={toggle}
        onText={setTxt}
        icon={sectionIcons.Throat}
        isEditing={isEditing}
      />

      <h4>{sectionIcons.NeckThyroid} Neck/Thyroid</h4>
      <Section
        id="NeckThyroid"
        label={sectionLabels.NeckThyroid}
        value={exam.NeckThyroid || { on: 0, text: "" }}
        onToggle={toggle}
        onText={setTxt}
        icon={sectionIcons.NeckThyroid}
        isEditing={isEditing}
      />

      <h4>{sectionIcons.Heart} Heart</h4>
      <Section
        id="Heart"
        label={sectionLabels.Heart}
        value={exam.Heart || { on: 0, text: "" }}
        onToggle={toggle}
        onText={setTxt}
        icon={sectionIcons.Heart}
        isEditing={isEditing}
      />

      <h4>{sectionIcons.Lungs} Lungs</h4>
      <Section
        id="Lungs"
        label={sectionLabels.Lungs}
        value={exam.Lungs || { on: 0, text: "" }}
        onToggle={toggle}
        onText={setTxt}
        icon={sectionIcons.Lungs}
        isEditing={isEditing}
      />

      <h4>{sectionIcons.Breast} Breast</h4>
      <Section
        id="Breast"
        label={sectionLabels.Breast}
        value={exam.Breast || { on: 0, text: "" }}
        onToggle={toggle}
        onText={setTxt}
        icon={sectionIcons.Breast}
        isEditing={isEditing}
      />

      <h4>{sectionIcons.Abdomen} Abdomen</h4>
      <Section
        id="Abdomen"
        label={sectionLabels.Abdomen}
        value={exam.Abdomen || { on: 0, text: "" }}
        onToggle={toggle}
        onText={setTxt}
        icon={sectionIcons.Abdomen}
        isEditing={isEditing}
      />

      <h4>{sectionIcons.Pelvic} Pelvic</h4>
      <Section
        id="Pelvic"
        label={sectionLabels.Pelvic}
        value={exam.Pelvic || { on: 0, text: "" }}
        onToggle={toggle}
        onText={setTxt}
        icon={sectionIcons.Pelvic}
        isEditing={isEditing}
      />

      <h4>{sectionIcons.back} Back</h4>
      <Section
        id="back"
        label={sectionLabels.back}
        value={exam.back || { on: 0, text: "" }}
        onToggle={toggle}
        onText={setTxt}
        icon={sectionIcons.back}
        isEditing={isEditing}
      />

      <h4>{sectionIcons.Musculoskeletal} Musculoskeletal</h4>
      <Section
        id="Musculoskeletal"
        label={sectionLabels.Musculoskeletal}
        value={exam.Musculoskeletal || { on: 0, text: "" }}
        onToggle={toggle}
        onText={setTxt}
        icon={sectionIcons.Musculoskeletal}
        isEditing={isEditing}
      />

      <h4>{sectionIcons.Skin} Skin</h4>
      <Section
        id="Skin"
        label={sectionLabels.Skin}
        value={exam.Skin || { on: 0, text: "" }}
        onToggle={toggle}
        onText={setTxt}
        icon={sectionIcons.Skin}
        isEditing={isEditing}
      />

      <h4>{sectionIcons.Neurologic} Neurologic</h4>
      <Section
        id="Neurologic"
        label={sectionLabels.Neurologic}
        value={exam.Neurologic || { on: 0, text: "" }}
        onToggle={toggle}
        onText={setTxt}
        icon={sectionIcons.Neurologic}
        isEditing={isEditing}
      />

      <h4>{sectionIcons.Extremities} Extremities</h4>
      <Section
        id="Extremities"
        label={sectionLabels.Extremities}
        value={exam.Extremities || { on: 0, text: "" }}
        onToggle={toggle}
        onText={setTxt}
        icon={sectionIcons.Extremities}
        isEditing={isEditing}
      />

      <h4>{sectionIcons.Psychiatric} Psychiatric</h4>
      <Section
        id="Psychiatric"
        label={sectionLabels.Psychiatric}
        value={exam.Psychiatric || { on: 0, text: "" }}
        onToggle={toggle}
        onText={setTxt}
        icon={sectionIcons.Psychiatric}
        isEditing={isEditing}
      />
    </div>
  );
}